import { useMemo } from 'react';

/**
 * Extract text from object with fallback keys
 */
export function extractText(item, fallbackKeys = ['text', 'option-text', 'right-text', 'left-text']) {
  let text = '';
  for (const key of fallbackKeys) {
    if (item[key]) {
      text = item[key];
      break;
    }
  }
  
  if (typeof text === 'object' && text !== null) {
    text = text.text || text['option-text'] || text['right-text'] || String(text);
  } else {
    text = String(text || '');
  }
  
  return text;
}

/**
 * Custom hook to build choices map for Pairing
 */
export function usePairingChoicesMap(question) {
  return useMemo(() => {
    const map = {};
    
    const choices = question.choices || question.pairs?.choices || [];
    if (Array.isArray(choices)) {
      choices.forEach((choice) => {
        const idx = choice.idx ?? choice.id;
        if (idx !== undefined) {
          const text = extractText(choice);
          if (text) {
            map[idx] = text;
          }
        }
      });
    }
    
    if (question.pairs?.right && Array.isArray(question.pairs.right)) {
      question.pairs.right.forEach((rightItem) => {
        const idx = rightItem.idx ?? rightItem.id;
        if (idx !== undefined && !map[idx]) {
          const text = extractText(rightItem, ['right-text', 'text']);
          if (text) {
            map[idx] = text;
          }
        }
      });
    }
    
    if (question.pairs?.left && Array.isArray(question.pairs.left)) {
      question.pairs.left.forEach((leftItem) => {
        const idx = leftItem.idx ?? leftItem.id;
        if (idx !== undefined && !map[idx]) {
          const text = extractText(leftItem, ['left-text', 'text']);
          if (text) {
            map[idx] = text;
          }
        }
      });
    }

    return map;
  }, [question]);
}

/**
 * Get right text by idx
 */
export function getRightText(rightIdx, choicesMap, question) {
  let rightText = choicesMap[rightIdx];
  
  if ((!rightText || rightText === '') && question.pairs?.right) {
    const rightItem = question.pairs.right.find((r) => (r.idx ?? r.id) === rightIdx);
    if (rightItem) {
      rightText = rightItem['right-text'] || rightItem.text || '';
    }
  }
  
  if (typeof rightText === 'object' && rightText !== null) {
    rightText = rightText.text || rightText['option-text'] || rightText['right-text'] || String(rightText);
  } else if (!rightText || rightText === '') {
    rightText = `[${rightIdx}]`;
  } else {
    rightText = String(rightText);
  }
  
  return rightText;
}

/**
 * Build displayPairs from question
 */
export function useDisplayPairs(question, choicesMap) {
  return useMemo(() => {
    if (!question.pairs?.left || !question.pairs?.right) {
      return [];
    }

    return question.pairs.left.map((leftItem) => {
      const leftIdx = leftItem.idx ?? leftItem.id;
      const rightItem = question.pairs.right.find((r) => (r.idx ?? r.id) === leftIdx);
      const rightIdx = rightItem ? (rightItem.idx ?? rightItem.id) : null;
      
      const leftText = extractText(leftItem, ['left-text', 'text']) || choicesMap[leftIdx] || '';
      const rightText = rightItem ? (extractText(rightItem, ['right-text', 'text']) || choicesMap[rightIdx] || '') : '';
      
      return {
        leftIdx,
        rightIdx,
        l: leftText,
        r: rightText,
      };
    });
  }, [question, choicesMap]);
}

/**
 * Parse userAnswer into map for Pairing
 */
export function usePairingUserAnswerMap(userAnswer, question, choicesMap) {
  return useMemo(() => {
    if (!userAnswer) {
      return {};
    }

    // Case 1: {left: [idx...], right: [idx...]}
    if (userAnswer?.left && userAnswer?.right && Array.isArray(userAnswer.left) && Array.isArray(userAnswer.right)) {
      const map = {};
      userAnswer.left.forEach((leftIdx, index) => {
        const rightIdx = userAnswer.right[index];
        if (leftIdx !== undefined && rightIdx !== undefined) {
          map[leftIdx] = {
            rightIdx,
            rightText: getRightText(rightIdx, choicesMap, question),
          };
        }
      });
      return map;
    }

    // Case 2: Array
    if (Array.isArray(userAnswer)) {
      // Case 2a: Array of objects dengan {left, right}
      if (userAnswer.length > 0 && typeof userAnswer[0] === 'object' && userAnswer[0] !== null && (userAnswer[0].left !== undefined || userAnswer[0].right !== undefined)) {
        const map = {};
        userAnswer.forEach((pairObj) => {
          const leftIdx = pairObj.left;
          const rightIdx = pairObj.right;
          
          if (leftIdx !== undefined && rightIdx !== undefined) {
            const rightIdxNum = typeof rightIdx === 'number' ? rightIdx : parseInt(rightIdx, 10);
            if (!isNaN(rightIdxNum)) {
              map[leftIdx] = {
                rightIdx: rightIdxNum,
                rightText: getRightText(rightIdxNum, choicesMap, question),
              };
            }
          }
        });
        return Object.keys(map).length > 0 ? map : {};
      }
      
      // Case 2b: Array of numbers
      if (question.pairs?.left) {
        const map = {};
        question.pairs.left.forEach((leftItem, index) => {
          const leftIdx = leftItem.idx ?? leftItem.id;
          const rightIdx = userAnswer[index];
          
          if (leftIdx !== undefined && rightIdx !== undefined) {
            const rightIdxNum = typeof rightIdx === 'number' ? rightIdx : (typeof rightIdx === 'object' ? (rightIdx.right ?? rightIdx.idx ?? rightIdx.id) : parseInt(rightIdx, 10));
            if (!isNaN(rightIdxNum)) {
              map[leftIdx] = {
                rightIdx: rightIdxNum,
                rightText: getRightText(rightIdxNum, choicesMap, question),
              };
            }
          }
        });
        return Object.keys(map).length > 0 ? map : {};
      }
    }

    return {};
  }, [userAnswer, choicesMap, question]);
}

